/* eslint-disable no-console */
import axios from "@bms/libs/axios";
import { ServiceCard } from "@bms/types";
import { isEmpty } from "lodash";
import { useCallback, useEffect, useState } from "react";
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormGetValues,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
  UseFormWatch,
  useFieldArray,
} from "react-hook-form";

export type ReceivedProps = {
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  reset: UseFormReset<FieldValues>;
  errors: FieldErrors<FieldValues>;
  control: Control<FieldValues>;
  isView: boolean;
  serviceCardData: ServiceCard;
};

const useWorkReport = (props: ReceivedProps) => {
  const { fields, append, remove } = useFieldArray({
    control: props.control,
    name: "work_report_attributes",
  });

  const [openModalUpload, setOpenModalUpload] = useState<string | null>(null);
  const [openModalDeleteImg, setOpenModalDeleteImg] = useState<string | null>(
    null
  );
  const [loadingImage, setLoadingImage] = useState<string | null>(null);
  const [extraPhotoCounts, setExtraPhotoCounts] = useState<
    Record<number, number>
  >({});
  const [openModalDeleteWorkItem, setOpenModalDeleteWorkItem] =
    useState<boolean>(false);
  const [removeWorkItemIdx, setRemoveWorkItemIdx] = useState<number | null>(
    null
  );
  const [openModalDeletePhotoBlock, setOpenModalDeletePhotoBlock] =
    useState<boolean>(false);
  const [removePhotoBlock, setRemovePhotoBlock] = useState<{
    workItemIndex: number;
    extraIndex: number | null;
  } | null>(null);
  const [hiddenMainPhotos, setHiddenMainPhotos] = useState<
    Record<number, boolean>
  >({});
  const [collapsedWorkItems, setCollapsedWorkItems] = useState<
    Record<number, boolean>
  >({});
  const [collapsedPhotoBlocks, setCollapsedPhotoBlocks] = useState<
    Record<string, boolean>
  >({});

  const photoBlockKey = (workItemIndex: number, extraIndex: number | null) =>
    `${workItemIndex}:${extraIndex === null ? "main" : extraIndex}`;

  const onOpenModalUpload = (name: string) => setOpenModalUpload(name);
  const onCloseModalUpload = () => setOpenModalUpload(null);
  const onOpenModalDeleteUpload = (name: string) => setOpenModalDeleteImg(name);
  const onCloseModalDeleteUpload = () => setOpenModalDeleteImg(null);

  const uploadImage = async (name: string, filePhoto: any) => {
    setLoadingImage(name);
    try {
      const formData = new FormData();
      formData.append("file_photo", filePhoto);

      const { data } = await axios({
        method: "POST",
        url: "/s3",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      props.setValue(name, data.url);
      props.setValue(`${name}_key`, data.key);
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoadingImage(null);
    }
  };

  const deleteImage = useCallback(async (name: string, key: string) => {
    setLoadingImage(name);
    try {
      await axios({
        method: "DELETE",
        url: "/s3/delete",
        data: {
          key,
        },
      });
      props.setValue(name, null);
      props.setValue(`${name}_key`, null);
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoadingImage(null);
    }
  }, []);

  useEffect(() => {
    if (props.reset && isEmpty(props.getValues("work_report_attributes"))) {
      props.reset({
        ...props.getValues(),
        work_report_attributes: [
          {
            title: "",
            before_photo: null,
            after_photo: null,
            comment: "",
          },
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addWorkItem = () => {
    append({
      title: "",
      before_photo: null,
      after_photo: null,
      comment: "",
      extra_photos: [],
    });
    setCollapsedWorkItems((prev) => ({
      ...prev,
      [fields.length]: false,
    }));
  };

  const toggleWorkItem = (index: number) => {
    setCollapsedWorkItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const togglePhotoBlock = (
    workItemIndex: number,
    extraIndex: number | null
  ) => {
    const key = photoBlockKey(workItemIndex, extraIndex);
    setCollapsedPhotoBlocks((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const onOpenModalDeleteWorkItem = (index: number) => {
    setRemoveWorkItemIdx(index);
    setOpenModalDeleteWorkItem(true);
  };

  const onCloseModalDeleteWorkItem = () => {
    setOpenModalDeleteWorkItem(false);
    setRemoveWorkItemIdx(null);
  };

  const confirmDeleteWorkItem = async () => {
    if (removeWorkItemIdx === null) return;

    remove(removeWorkItemIdx);
    if (fields.length <= 1) {
      append({
        title: "",
        before_photo: null,
        after_photo: null,
        comment: "",
        extra_photos: [],
      });
    }
    const shiftIndexedState = (
      prev: Record<number, any>,
      removedIndex: number
    ) => {
      const next: Record<number, any> = {};
      Object.keys(prev).forEach((key) => {
        const currentIndex = Number(key);
        if (currentIndex < removedIndex) {
          next[currentIndex] = prev[currentIndex];
        } else if (currentIndex > removedIndex) {
          next[currentIndex - 1] = prev[currentIndex];
        }
      });
      return next;
    };

    setExtraPhotoCounts((prev) => shiftIndexedState(prev, removeWorkItemIdx));
    setHiddenMainPhotos((prev) => shiftIndexedState(prev, removeWorkItemIdx));
    setCollapsedWorkItems((prev) => shiftIndexedState(prev, removeWorkItemIdx));
    setCollapsedPhotoBlocks((prev) => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach((key) => {
        const [workIndexText, rest] = key.split(":");
        const workIndex = Number(workIndexText);
        if (workIndex < removeWorkItemIdx) {
          next[key] = prev[key];
        } else if (workIndex > removeWorkItemIdx) {
          next[`${workIndex - 1}:${rest}`] = prev[key];
        }
      });
      return next;
    });
    onCloseModalDeleteWorkItem();
  };

  const onOpenModalDeletePhotoBlock = (
    workItemIndex: number,
    extraIndex: number | null
  ) => {
    setRemovePhotoBlock({ workItemIndex, extraIndex });
    setOpenModalDeletePhotoBlock(true);
  };

  const onCloseModalDeletePhotoBlock = () => {
    setOpenModalDeletePhotoBlock(false);
    setRemovePhotoBlock(null);
  };

  const confirmDeletePhotoBlock = async () => {
    if (!removePhotoBlock) return;

    const { workItemIndex, extraIndex } = removePhotoBlock;
    const prefix = `work_report_attributes.${workItemIndex}`;

    const shiftPhotoBlockKeys = (
      extrasLength: number,
      removedExtraIndex: number | null
    ) => {
      setCollapsedPhotoBlocks((prev) => {
        const next = { ...prev };
        if (removedExtraIndex === null) {
          if (extrasLength) {
            next[photoBlockKey(workItemIndex, null)] =
              prev[photoBlockKey(workItemIndex, 0)];
            delete next[photoBlockKey(workItemIndex, 0)];
            for (let i = 1; i < extrasLength; i += 1) {
              next[photoBlockKey(workItemIndex, i - 1)] =
                prev[photoBlockKey(workItemIndex, i)];
              delete next[photoBlockKey(workItemIndex, i)];
            }
          } else {
            delete next[photoBlockKey(workItemIndex, null)];
          }
        } else {
          delete next[photoBlockKey(workItemIndex, removedExtraIndex)];
          for (let i = removedExtraIndex + 1; i < extrasLength; i += 1) {
            next[photoBlockKey(workItemIndex, i - 1)] =
              prev[photoBlockKey(workItemIndex, i)];
            delete next[photoBlockKey(workItemIndex, i)];
          }
        }
        return next;
      });
    };

    if (extraIndex === null) {
      const extras = props.getValues(`${prefix}.extra_photos`) || [];
      shiftPhotoBlockKeys(extras.length, null);
      if (extras.length) {
        const [first, ...rest] = extras;
        props.setValue(`${prefix}.before_photo`, first.before_photo ?? null, {
          shouldDirty: true,
        });
        props.setValue(`${prefix}.after_photo`, first.after_photo ?? null, {
          shouldDirty: true,
        });
        props.setValue(
          `${prefix}.before_photo_key`,
          first.before_photo_key ||
            first.before_photo?.before_photo_key ||
            null,
          { shouldDirty: true }
        );
        props.setValue(
          `${prefix}.after_photo_key`,
          first.after_photo_key || first.after_photo?.after_photo_key || null,
          { shouldDirty: true }
        );
        props.setValue(`${prefix}.extra_photos`, rest, { shouldDirty: true });
        setExtraPhotoCounts((prev) => ({
          ...prev,
          [workItemIndex]: rest.length,
        }));
      } else {
        props.setValue(`${prefix}.before_photo`, null, { shouldDirty: true });
        props.setValue(`${prefix}.after_photo`, null, { shouldDirty: true });
        props.setValue(`${prefix}.before_photo_key`, null, {
          shouldDirty: true,
        });
        props.setValue(`${prefix}.after_photo_key`, null, {
          shouldDirty: true,
        });
        props.setValue(`${prefix}.delete_before_photo`, true, {
          shouldDirty: true,
        });
        props.setValue(`${prefix}.delete_after_photo`, true, {
          shouldDirty: true,
        });
        setHiddenMainPhotos((prev) => ({ ...prev, [workItemIndex]: true }));
      }
    } else {
      const extras = [...(props.getValues(`${prefix}.extra_photos`) || [])];
      shiftPhotoBlockKeys(extras.length, extraIndex);
      extras.splice(extraIndex, 1);
      props.setValue(`${prefix}.extra_photos`, extras, { shouldDirty: true });
      setExtraPhotoCounts((prev) => ({
        ...prev,
        [workItemIndex]: extras.length,
      }));
    }

    onCloseModalDeletePhotoBlock();
  };

  const addPhotoPair = (index: number) => {
    if (hiddenMainPhotos[index]) {
      setHiddenMainPhotos((prev) => ({ ...prev, [index]: false }));
      return;
    }

    const extraPhotos =
      props.getValues(`work_report_attributes.${index}.extra_photos`) || [];
    const nextPhotos = [
      ...extraPhotos,
      {
        before_photo: null,
        after_photo: null,
      },
    ];
    props.setValue(`work_report_attributes.${index}.extra_photos`, nextPhotos, {
      shouldDirty: true,
    });
    setExtraPhotoCounts((prev) => ({
      ...prev,
      [index]: nextPhotos.length,
    }));
  };

  return {
    ...props,
    fields,
    openModalUpload,
    openModalDeleteImg,
    onOpenModalDeleteUpload,
    onCloseModalDeleteUpload,
    onOpenModalUpload,
    onCloseModalUpload,
    uploadImage,
    deleteImage,
    loadingImage,
    extraPhotoCounts,
    hiddenMainPhotos,
    collapsedWorkItems,
    collapsedPhotoBlocks,
    addPhotoPair,
    addWorkItem,
    toggleWorkItem,
    togglePhotoBlock,
    openModalDeleteWorkItem,
    onOpenModalDeleteWorkItem,
    onCloseModalDeleteWorkItem,
    confirmDeleteWorkItem,
    openModalDeletePhotoBlock,
    onOpenModalDeletePhotoBlock,
    onCloseModalDeletePhotoBlock,
    confirmDeletePhotoBlock,
  };
};

export type Props = ReturnType<typeof useWorkReport>;

export default useWorkReport;
